import { useNavigate, useLocation } from 'react-router-dom';
import { useCallback, useEffect } from 'react';

// Define the order of story pages
const STORY_PAGES = [
  '/insights/spending',
  '/insights/top-purchases',
  '/insights/monthly-spending',
  '/insights/ride-or-die',
  '/insights/favorite-time',
  '/insights/spending-categories',
  '/insights/expensive-day',
  '/insights/reality-check',
  '/insights/top-streaks',
  '/insights/income-gap',
  '/insights/money-wrapped'
];

export const useStoryNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const currentIndex = STORY_PAGES.indexOf(location.pathname);

  const goToNext = useCallback(() => {
    if (currentIndex < STORY_PAGES.length - 1) {
      navigate(STORY_PAGES[currentIndex + 1]);
    }
  }, [currentIndex, navigate]);

  const goToPrev = useCallback(() => {
    if (currentIndex > 0) {
      navigate(STORY_PAGES[currentIndex - 1]);
    }
  }, [currentIndex, navigate]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrev();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [goToNext, goToPrev]);

  // Handle touch navigation
  useEffect(() => {
    let touchStartX = 0;
    const threshold = 50; // minimum distance for swipe

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX;
      const diff = touchEndX - touchStartX;

      if (Math.abs(diff) >= threshold) {
        if (diff > 0) {
          goToPrev();
        } else {
          goToNext();
        }
      }
    };

    window.addEventListener('touchstart', handleTouchStart);
    window.addEventListener('touchend', handleTouchEnd);

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [goToNext, goToPrev]);

  return {
    goToNext,
    goToPrev,
    hasNext: currentIndex < STORY_PAGES.length - 1,
    hasPrev: currentIndex > 0,
    currentIndex,
    totalStories: STORY_PAGES.length
  };
};
